import { ReactNode } from 'react';
import { BuyerSidebar } from '@/components/buyer/BuyerSidebar';
import { SupplierSidebar } from '@/components/supplier/SupplierSidebar';
import { BuyerTopNav } from '@/components/buyer/BuyerTopNav';
import { SupplierTopNav } from '@/components/supplier/SupplierTopNav';
import { BuyerMobileHeader } from '@/components/buyer/BuyerMobileHeader';
import { SupplierMobileHeader } from '@/components/supplier/SupplierMobileHeader';
import { MobileBottomNav } from '@/components/navigation/MobileBottomNav';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface LayoutWrapperProps {
  children: ReactNode;
  role: 'buyer' | 'supplier';
}

function LayoutWrapperContent({ children, role }: LayoutWrapperProps) {
  const { isCollapsed } = useSidebar();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="flex min-h-dvh w-full flex-col bg-background">
        <header>
          {role === 'buyer' ? <BuyerMobileHeader /> : <SupplierMobileHeader />}
        </header>
        <main
          id="main-content"
          data-scroll-container
          className="ios-smooth-scroll flex-1 overflow-y-auto bg-background px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:px-6 lg:px-8"
        >
          {children}
        </main>
        <nav aria-label="Mobile navigation">
          <MobileBottomNav />
        </nav>
      </div>
    );
  }

  return (
    <div 
      className="flex h-screen overflow-hidden bg-background" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        zIndex: 1
      }}
    >
      <aside
        className={cn(
          "hidden md:flex bg-white transition-all duration-250 ease-in-out border-r border-border h-screen flex-shrink-0",
          isCollapsed ? "w-[72px]" : "w-[280px]"
        )}
        aria-label="Main navigation"
      >
        <div className="sticky top-0 h-screen w-full">
          {role === 'buyer' ? <BuyerSidebar /> : <SupplierSidebar />}
        </div>
      </aside>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 sm:px-6 lg:px-8">
        <header className="hidden md:block">
          {role === 'buyer' ? <BuyerTopNav /> : <SupplierTopNav />}
        </header>
        <main
          id="main-content"
          data-scroll-container
          className="ios-smooth-scroll min-h-0 flex-1 overflow-y-auto bg-background pb-8 pt-4 md:pt-6"
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export function LayoutWrapper({ children, role }: LayoutWrapperProps) {
  return (
    <SidebarProvider>
      <LayoutProvider>
        <LayoutWrapperContent role={role}>{children}</LayoutWrapperContent>
      </LayoutProvider>
    </SidebarProvider>
  );
}

